import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mintyPromotionBriefs } from "../../shared/mintyPromotion";
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clapperboard,
  Clock3,
  Copy,
  Download,
  FileImage,
  Film,
  FolderOpen,
  GalleryHorizontalEnd,
  Image,
  Images,
  Layers3,
  Library,
  LockKeyhole,
  Package,
  Play,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Upload,
  WandSparkles,
} from "lucide-react";
import { api, post, upload } from "../api";
import type {
  Bootstrap,
  IntelligenceItem,
  MediaAsset,
  MediaCampaignPackage,
  MediaJob,
} from "../types";
import {
  Card,
  Empty,
  Eyebrow,
  PageIntro,
  Score,
  Status,
} from "../components/UI";

type Props = { data: Bootstrap; refresh: () => Promise<void> };
type CampaignChannel = "X" | "INSTAGRAM" | "TELEGRAM";
type DistributionOutcome = {
  platform: CampaignChannel;
  contentId: string | null;
  status: string;
  error: string | null;
};
type CampaignDetail = {
  campaign: MediaCampaignPackage;
  assets: MediaAsset[];
  jobs: MediaJob[];
  readiness: { ready: boolean; missing: string[]; activeJobs: number; failedJobs: number; expectedInstagramSlides: number; readyInstagramSlideIds: string[]; canUseReducedCarousel: boolean; acceptedReduction: { plannedCount: number; acceptedCount: number; assetIds: string[] } | null };
};
const campaignChannels: CampaignChannel[] = ["X", "INSTAGRAM", "TELEGRAM"];
const formats = [
  {
    key: "FULL_CAMPAIGN",
    label: "Full campaign",
    body: "Copy, image, meme, carousel and video storyboard.",
    icon: Package,
  },
  {
    key: "IMAGE",
    label: "Image",
    body: "A premium social visual in the right ratio.",
    icon: Image,
  },
  {
    key: "MEME",
    label: "Meme",
    body: "One sharp Minty-native concept, not template spam.",
    icon: WandSparkles,
  },
  {
    key: "CAROUSEL",
    label: "Carousel",
    body: "A complete, editable six-slide story.",
    icon: GalleryHorizontalEnd,
  },
  {
    key: "SHORT_VIDEO",
    label: "Short video",
    body: "Storyboard first. Rendering only after approval.",
    icon: Clapperboard,
  },
  {
    key: "TEXT",
    label: "Text",
    body: "Write, check and approve platform copy.",
    icon: FileImage,
  },
] as const;

export function CreateHome({ data }: Props) {
  const navigate = useNavigate();
  const thresholds = {
    creativeReview: 68,
    mintyFit: 65,
    ...(data.settings.creative_thresholds ?? {}),
  };
  const hot = data.intelligence.find(
    (item) =>
      item.editorial_policy?.eligible !== false &&
      item.recommendation === "CREATE NOW" &&
      item.viral_score >= thresholds.creativeReview &&
      item.minty_fit_score >= thresholds.mintyFit &&
      item.estimated_freshness !== "STALE" &&
      item.risk_rating !== "HIGH" &&
      item.source_count > 0,
  );
  const best =
    hot ??
    [...data.intelligence].filter((item) => item.editorial_policy?.eligible !== false).sort((a, b) => b.viral_score - a.viral_score)[0];
  return (
    <>
      <PageIntro
        eyebrow="CREATE"
        title="Minty makes the campaign."
        body="Promote MINTY or choose a story. Every campaign still needs your review and approval."
      />
      <Card>
        <Eyebrow>PROMOTE MINTY</Eyebrow>
        <h2>Bring people into the Galaxy.</h2>
        <p>The cinematic direction you approved: graphite, violet, one strong idea. Brand-first campaigns, not just crypto news.</p>
        <div className="make-grid">
          {mintyPromotionBriefs.map(brief => (
            <button key={brief.id} onClick={() => navigate(`/create/media?mode=idea&type=FULL_CAMPAIGN&promotion=${brief.id}`)}>
              <strong>{brief.label}</strong><small>{brief.description}</small><ChevronRight />
            </button>
          ))}
        </div>
        <p>Review the idea first. Nothing is approved or published automatically.</p>
      </Card>
      {best && (
        <Card className={`media-recommend ${hot ? "hot" : ""}`}>
          <div>
            <Eyebrow>
              {hot
                ? "RECOMMENDED FROM TODAY'S HOT CASE"
                : "BEST CURRENT LEAD · NO PAID MEDIA RECOMMENDED"}
            </Eyebrow>
            <h2>{best.title}</h2>
            <p>
              {best.angle?.detectiveAngle ??
                best.visual_concept ??
                best.summary}
            </p>
            <div className="media-recommend-row">
              <span>
                <Sparkles /> Best format
              </span>
              <strong>
                {best.recommended_media_type ??
                  (best.viral_score >= 80
                    ? "MEME + 15-SECOND REEL"
                    : "TEMPLATE IMAGE")}
              </strong>
              <span>
                <CircleDollarSign /> Estimated generation
              </span>
              <strong>$0.00 locally</strong>
            </div>
          </div>
          <Score value={best.viral_score} size="lg" />
          <button
            className="button button-primary button-large"
            onClick={() => navigate(`/create/media?story=${best.id}`)}
          >
            CREATE <ArrowRight />
          </button>
        </Card>
      )}
      <button
        className="own-idea"
        onClick={() => navigate("/create/media?mode=idea&type=FULL_CAMPAIGN")}
      >
        <Plus />
        <span>
          <strong>Start from my own idea</strong>
          <small>Bring the thought. Minty will structure it.</small>
        </span>
        <ChevronRight />
      </button>
      <details className="technical-panel">
        <summary>
          Advanced creation tools <ChevronRight />
        </summary>
        <div className="make-grid">
          {formats.map(({ key, label, body, icon: Icon }) => (
            <button
              key={key}
              onClick={() =>
                key === "TEXT"
                  ? navigate("/create/text")
                  : navigate(
                      `/create/media?type=${key}${best ? `&story=${best.id}` : ""}`,
                    )
              }
            >
              <span>
                <Icon />
              </span>
              <strong>{label}</strong>
              <small>{body}</small>
              <ChevronRight />
            </button>
          ))}
        </div>
      </details>
    </>
  );
}

export function MediaStudio({ data, refresh }: Props) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get("type") ?? "FULL_CAMPAIGN");
  const [sourceMode, setSourceMode] = useState<"case" | "idea">(
    params.get("mode") === "idea" ? "idea" : "case",
  );
  const [storyId, setStoryId] = useState(
    params.get("story") ?? data.intelligence[0]?.id ?? "",
  );
  const [idea, setIdea] = useState(() => mintyPromotionBriefs.find(brief => brief.id === params.get("promotion"))?.title ?? "");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [ratio, setRatio] = useState("4:5");
  const [template, setTemplate] = useState("CASE_FILE");
  const [platform, setPlatform] = useState("INSTAGRAM");
  const [destination, setDestination] = useState("INSTAGRAM");
  const [feedback, setFeedback] = useState<string[]>([]);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [overrideReason, setOverrideReason] = useState("EDITORIAL_JUDGEMENT");
  const [overrideNote, setOverrideNote] = useState("");
  const [overrideIdempotencyKey] = useState(() => crypto.randomUUID());
  const [selectedChannels, setSelectedChannels] = useState<CampaignChannel[]>([]);
  const [distributionStage, setDistributionStage] = useState<"choose" | "confirm" | "result">("choose");
  const [timing, setTiming] = useState<"now" | "schedule">("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [outcomes, setOutcomes] = useState<DistributionOutcome[]>([]);
  const [draggedSlide, setDraggedSlide] = useState<string | null>(null);
  const autoStarted = useRef(false);
  const selectionInitialised = useRef<string | null>(null);
  const packageId = params.get("package");
  const overrideMode = params.get("override") === "1";
  const [campaignDetail, setCampaignDetail] = useState<CampaignDetail | null>(null);
  const [detailLoadError, setDetailLoadError] = useState(false);
  const loadCampaignDetail = useCallback(async () => {
    if (!packageId) {
      setCampaignDetail(null);
      setDetailLoadError(false);
      return;
    }
    try {
      setCampaignDetail(await api<CampaignDetail>(`/media/campaigns/${packageId}`));
      setDetailLoadError(false);
    } catch {
      // A transient network failure must not replace live job state with an
      // older bootstrap snapshot and silently stop the recovery poll.
      setDetailLoadError(true);
    }
  }, [packageId]);
  useEffect(() => { void loadCampaignDetail(); }, [loadCampaignDetail]);
  const campaign = campaignDetail?.campaign ??
    data.media.packages.find((item) => item.id === packageId) ?? null;
  const assets = (campaignDetail?.assets ?? data.media.assets
    .filter((item) => item.campaign_package_id === campaign?.id)
  ).sort((a, b) => slideOrder(a) - slideOrder(b));
  const platformAssets = (name: "X" | "INSTAGRAM" | "TELEGRAM") => {
    const exact = assets.filter((asset) => asset.platform === name);
    const selected = name === "INSTAGRAM" && !exact.length
      ? assets.filter((asset) => asset.platform === "GENERAL" && asset.asset_type !== "HERO_ART")
      : exact;
    if (name !== "INSTAGRAM" || !campaign?.carousel_order?.length) return selected;
    const positions = new Map(campaign.carousel_order.map((id, index) => [id, index]));
    return [...selected].sort((a, b) => (positions.get(a.id) ?? 999) - (positions.get(b.id) ?? 999) || slideOrder(a) - slideOrder(b));
  };
  const platformConnection = (name: "X" | "INSTAGRAM" | "TELEGRAM") =>
    data.bridge.accounts.find((account) => account.platform.toUpperCase() === name)?.connection_health.state ?? "DISCONNECTED";
  const jobs = campaignDetail?.jobs ?? data.media.jobs.filter(
    (item) => item.campaign_package_id === campaign?.id,
  );
  const activeJobs = jobs.some((item) =>
    ["QUEUED", "PREPARING", "GENERATING", "RENDERING"].includes(item.state),
  );
  const activeJobCount = jobs.filter((item) => ["QUEUED", "PREPARING", "GENERATING", "RENDERING"].includes(item.state)).length;
  const oldestActiveSeconds = activeJobs ? Math.max(...jobs.filter((item) => ["QUEUED", "PREPARING", "GENERATING", "RENDERING"].includes(item.state)).map(item => Math.max(0, (Date.now() - new Date(item.created_at).getTime()) / 1000))) : 0;
  const missingMedia = campaignDetail?.readiness.missing ??
    (campaign?.lead_format !== "TEXT" && !assets.length ? ["lead visual"] : []);
  const uncheckedVisuals = assets.filter(asset => !['PASS', 'ADVISORY'].includes(asset.claim_check?.status ?? ''));
  const approvalBlocker = activeJobs
    ? "Minty is still creating the finished media."
    : missingMedia.length
      ? `Media is incomplete: ${missingMedia.join(", ")}.`
      : null;
  const distributionStates = outcomes.map((outcome) => {
    const job = data.publishJobs.find(
      (candidate) => candidate.content_item_id === outcome.contentId,
    );
    return {
      ...outcome,
      observed: job?.status ?? outcome.status,
      postUrl: job?.platform_post_url ?? null,
    };
  });
  const resultHasFailures = distributionStates.some((item) =>
    ["FAILED", "ERROR", "NOT_SENT"].includes(item.observed),
  );
  const failedChannelCount = distributionStates.filter((item) => ["FAILED", "ERROR", "NOT_SENT"].includes(item.observed)).length;
  const resultHasSuccess = distributionStates.some(
    (item) => !["FAILED", "ERROR", "NOT_SENT"].includes(item.observed),
  );
  const resultAllLive =
    distributionStates.length > 0 &&
    distributionStates.every((item) => item.observed === "PUBLISHED");
  useEffect(() => {
    if (!packageId || campaign?.status === "APPROVED" || campaign?.status === "PUBLISHED") return;
    const timer = setInterval(() => { void loadCampaignDetail(); }, 3000);
    return () => clearInterval(timer);
  }, [packageId, campaign?.status, loadCampaignDetail]);
  useEffect(() => {
    if (
      distributionStage !== "result" ||
      timing === "schedule" ||
      !distributionStates.some((item) =>
        ["RECEIVED", "QUEUED", "PROCESSING", "SENDING"].includes(item.observed),
      )
    )
      return;
    const timer = setInterval(() => refresh(), 5000);
    return () => clearInterval(timer);
  }, [distributionStage, timing, distributionStates, refresh]);
  const story = data.intelligence.find((item) => item.id === storyId);
  const channelReady = (name: CampaignChannel) => {
    const account = data.bridge.accounts.find(
      (candidate) => candidate.platform.toUpperCase() === name,
    );
    const connected =
      account?.active && account.connection_health.state === "HEALTHY";
    const hasCreative =
      campaign?.lead_format === "TEXT" || platformAssets(name).length > 0;
    return Boolean(connected && hasCreative);
  };
  const channelReason = (name: CampaignChannel) => {
    const account = data.bridge.accounts.find(
      (candidate) => candidate.platform.toUpperCase() === name,
    );
    if (!account || !account.active) return `${name} is not connected.`;
    if (account.connection_health.state !== "HEALTHY")
      return `${name} needs reconnecting.`;
    if (campaign?.lead_format !== "TEXT" && !platformAssets(name).length)
      return `${name} media is not ready.`;
    return "Ready";
  };
  useEffect(() => {
    if (!campaign || campaign.status !== "APPROVED") return;
    if (selectionInitialised.current === campaign.id) return;
    selectionInitialised.current = campaign.id;
    setSelectedChannels(campaignChannels.filter(channelReady));
    setDistributionStage("choose");
  }, [campaign?.id, campaign?.status, data.bridge.accounts, assets.length]);

  async function createCampaign() {
    setBusy("campaign");
    setNotice("");
    try {
      const made = overrideMode && sourceMode === "case"
        ? await post<{ id: string; decision: string }>(`/intelligence/${storyId}/create-anyway`, {
          requestedType: type,
          reasonCode: overrideReason,
          note: overrideNote.trim() || null,
          idempotencyKey: overrideIdempotencyKey,
        })
        : await post<{ id: string; decision: string }>(
          "/media/campaigns",
          {
            intelligenceItemId: sourceMode === "case" ? storyId : null,
            idea: sourceMode === "idea" ? idea : null,
            promotionId: sourceMode === "idea" ? (params.get("promotion") || undefined) : undefined,
            requestedType: type,
          },
        );
      await refresh();
      setParams({ package: made.id });
      setNotice(
        made.decision === "CREATE"
          ? overrideMode
            ? "Your override is recorded. Minty built the campaign; evidence, claim checks and approval still apply."
            : "Minty built the campaign. Review the finished work below."
          : `Creative Director decision: ${made.decision}. Nothing was generated or published.`,
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  useEffect(() => {
    if (
      packageId ||
      sourceMode !== "case" ||
      !params.get("story") ||
      overrideMode ||
      autoStarted.current
    )
      return;
    autoStarted.current = true;
    void createCampaign();
  }, [packageId, sourceMode, storyId, overrideMode]);
  async function render(concept?: {
    overlay: string;
    concept: string;
    sourceAssetId?: string;
  }) {
    if (!campaign) return;
    setBusy("render");
    setNotice("");
    try {
      await post("/media/jobs", {
        campaignPackageId: campaign.id,
        caseId: campaign.case_id,
        title: concept?.concept ?? campaign.title,
        overlay: concept?.overlay ?? campaign.title,
        templateKey: template,
        aspectRatio: ratio,
        assetType:
          concept && !concept.sourceAssetId
            ? "MEME"
            : type === "MEME"
              ? "MEME"
              : type === "CAROUSEL"
                ? "CAROUSEL_SLIDE"
                : "IMAGE",
        provenance: "TEMPLATE GENERATED",
        sourceAssetId: concept?.sourceAssetId,
        idempotencyKey: `${campaign.id}:${template}:${ratio}:${crypto.randomUUID()}`,
        isDemo: campaign.is_demo,
      });
      await refresh();
      setNotice("Rendering started. You can safely leave this page.");
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function assetAction(
    asset: MediaAsset,
    action: "check" | "approve" | "handoff" | "archive",
  ) {
    setBusy(`${action}-${asset.id}`);
    setNotice("");
    try {
      await post(`/media/assets/${asset.id}/${action}`);
      await refresh();
      setNotice(
        action === "check"
          ? "Visual claim check complete."
          : action === "approve"
            ? "This exact version is approved."
            : action === "handoff"
              ? "Media prepared securely inside DISTRO-NINE. Nothing was published."
              : "Asset archived.",
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function checkCampaignVisuals() {
    setBusy('check-campaign-visuals');
    setNotice('');
    try {
      for (const asset of uncheckedVisuals) await post(`/media/assets/${asset.id}/check`);
      await refresh();
      await loadCampaignDetail();
      setNotice('Visual checks complete. Review any flags and the body copy before approving.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Visual checking failed. Your campaign is safe.');
    } finally { setBusy(''); }
  }
  async function prepareContent() {
    if (!campaign) return;
    setBusy("content");
    setNotice("");
    try {
      const readyAssets = assets
        .filter((asset) => asset.status === "APPROVED" && asset.distro_media_id)
        .map((asset) => asset.id);
      const result = await post<{ id: string }>(
        `/media/campaigns/${campaign.id}/content`,
        { platform: destination, assetIds: readyAssets },
      );
      await refresh();
      navigate(`/create/text?content=${result.id}`);
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function approveCampaign() {
    if (!campaign) return;
    setBusy("approve-campaign");
    setNotice("");
    try {
      await post(`/media/campaigns/${campaign.id}/approve`);
      for (const asset of assets) {
        if (!asset.distro_media_id)
          await post(`/media/assets/${asset.id}/handoff`);
      }
      await Promise.all([refresh(), loadCampaignDetail()]);
      selectionInitialised.current = null;
      setDistributionStage("choose");
      setNotice(
        "Approved. Everything is checked and ready.",
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function saveCarouselOrder(assetIds: string[]) {
    if (!campaign) return;
    setBusy("carousel-order");
    setNotice("");
    try {
      await post(`/media/campaigns/${campaign.id}/carousel-order`, { assetIds });
      await Promise.all([refresh(), loadCampaignDetail()]);
      setNotice("Instagram carousel order saved. This exact sequence will be sent to DISTRO-NINE after approval.");
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
      setDraggedSlide(null);
    }
  }
  function moveCarouselSlide(assetId: string, offset: -1 | 1) {
    const ordered = platformAssets("INSTAGRAM");
    const from = ordered.findIndex((asset) => asset.id === assetId);
    const to = from + offset;
    if (from < 0 || to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    void saveCarouselOrder(next.map((asset) => asset.id));
  }
  function dropCarouselSlide(targetId: string) {
    if (!draggedSlide || draggedSlide === targetId) return setDraggedSlide(null);
    const ordered = platformAssets("INSTAGRAM");
    const from = ordered.findIndex((asset) => asset.id === draggedSlide);
    const to = ordered.findIndex((asset) => asset.id === targetId);
    if (from < 0 || to < 0) return setDraggedSlide(null);
    const next = [...ordered];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    void saveCarouselOrder(next.map((asset) => asset.id));
  }
  function chooseTiming(next: "now" | "schedule") {
    setTiming(next);
    setNotice("");
    if (next === "now") setDistributionStage("confirm");
  }
  async function executeDistribution() {
    if (!campaign || !selectedChannels.length) return;
    setBusy("distribution");
    setNotice("");
    const results: DistributionOutcome[] = [];
    const scheduleIso =
      timing === "schedule" && scheduledAt
        ? new Date(scheduledAt).toISOString()
        : null;
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Dubai";
    for (const channel of selectedChannels) {
      let contentId: string | null = null;
      try {
        const account = data.bridge.accounts.find(
          (candidate) =>
            candidate.platform.toUpperCase() === channel &&
            candidate.active &&
            candidate.connection_health.state === "HEALTHY",
        );
        if (!account) throw new Error(`${channel} needs reconnecting.`);
        const assetIds = platformAssets(channel)
          .filter(
            (asset) =>
              asset.status === "APPROVED" && Boolean(asset.distro_media_id),
          )
          .map((asset) => asset.id);
        if (campaign.lead_format !== "TEXT" && !assetIds.length)
          throw new Error(`${channel} media is not ready.`);
        const prepared = await post<{ id: string }>(
          `/media/campaigns/${campaign.id}/content`,
          { platform: channel, assetIds },
        );
        contentId = prepared.id;
        const checked = await post<{ status: string }>(
          `/content/${contentId}/check`,
        );
        if (checked.status === "BLOCKED")
          throw new Error(`${channel} needs a claim corrected before posting.`);
        await post(`/content/${contentId}/transition`, {
          status: "APPROVED",
          notes: `Approved with campaign ${campaign.id}, version ${campaign.version}.`,
        });
        if (scheduleIso)
          await post(`/content/${contentId}/transition`, {
            status: "SCHEDULED",
            scheduledAt: scheduleIso,
          });
        const sent = await post<{ response: { status: string } }>(
          `/content/${contentId}/publish`,
          { account: account.alias, timezone },
        );
        results.push({
          platform: channel,
          contentId,
          status: sent.response.status,
          error: null,
        });
      } catch (error) {
        results.push({
          platform: channel,
          contentId,
          status: "FAILED",
          error:
            error instanceof Error
              ? error.message.replace(/^.*?:\s*/, "")
              : `${channel} needs attention.`,
        });
      }
    }
    setOutcomes(results);
    await refresh();
    setDistributionStage("result");
    setBusy("");
  }
  async function retryDistribution(outcome: DistributionOutcome) {
    if (!outcome.contentId) return setDistributionStage("choose");
    const account = data.bridge.accounts.find(
      (candidate) =>
        candidate.platform.toUpperCase() === outcome.platform &&
        candidate.active &&
        candidate.connection_health.state === "HEALTHY",
    );
    if (!account) {
      setNotice(`${outcome.platform} needs reconnecting.`);
      return;
    }
    setBusy(`retry-${outcome.platform}`);
    try {
      const sent = await post<{ response: { status: string } }>(
        `/content/${outcome.contentId}/publish`,
        {
          account: account.alias,
          timezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Dubai",
        },
      );
      setOutcomes((current) =>
        current.map((item) =>
          item.platform === outcome.platform
            ? { ...item, status: sent.response.status, error: null }
            : item,
        ),
      );
      await refresh();
    } catch {
      setNotice(`${outcome.platform} still needs attention. Try again shortly.`);
    } finally {
      setBusy("");
    }
  }
  async function tryAgain() {
    if (!campaign || (!feedback.length && !feedbackNote.trim())) return;
    setBusy("revise");
    setNotice("");
    try {
      const made = await post<{ id: string }>(
        `/media/campaigns/${campaign.id}/revise`,
        { feedback, note: feedbackNote.trim() || null },
      );
      await refresh();
      setParams({ package: made.id });
      setFeedback([]);
      setFeedbackNote("");
      setNotice(
        "Minty created a new version. The previous version is preserved.",
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function regenerateMedia() {
    if (!campaign) return;
    setBusy("regenerate");
    setNotice("");
    try {
      await post(`/media/campaigns/${campaign.id}/regenerate`);
      await Promise.all([refresh(), loadCampaignDetail()]);
      setNotice(
        "Minty is rebuilding the complete finished media set. The previous draft is safely archived.",
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function retryMissingMedia() {
    if (!campaign) return;
    setBusy("retry-missing");
    setNotice("");
    try {
      await post(`/media/campaigns/${campaign.id}/retry-missing`);
      await Promise.all([refresh(), loadCampaignDetail()]);
      setNotice("Minty is retrying only the missing media. Your finished assets are safe.");
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function useReadyCarousel() {
    if (!campaign) return;
    setBusy("use-ready-carousel");
    setNotice("");
    try {
      const result = await post<{ acceptedSlides: number; plannedSlides: number }>(`/media/campaigns/${campaign.id}/use-ready-carousel`);
      await Promise.all([refresh(), loadCampaignDetail()]);
      setNotice(`${result.acceptedSlides} of ${result.plannedSlides} Instagram slides are now the approved package plan. Arrange them, then review and approve.`);
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }

  if (!campaign && sourceMode === "case" && Boolean(params.get("story"))) {
    if (overrideMode && story)
      return (
        <>
          <button className="back-link" onClick={() => navigate("/discover")}><ArrowLeft /> Back to Discover</button>
          <PageIntro eyebrow="OPERATOR DECISION" title="Create anyway?" body="Minty’s recommendation is preserved. This records your editorial decision; it does not bypass evidence, safety, claim checks or human approval." />
          <Card className="campaign-brief">
            <div><Eyebrow>MINTY RECOMMENDS</Eyebrow><h2>{story.recommendation} · {story.viral_score}</h2><p>{story.title}</p></div>
            <div><Eyebrow>WHAT STAYS PROTECTED</Eyebrow><p>Source evidence, freshness, legal-risk checks, visual claim checks, human approval and DISTRO-NINE validation.</p></div>
          </Card>
          <Card className="campaign-decision">
            <div><Eyebrow>YOUR REASON</Eyebrow><h2>Why make this now?</h2><p>This is saved to the audit trail with Minty’s original recommendation.</p></div>
            <div className="feedback-grid">
              <select value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} aria-label="Override reason">
                <option value="EDITORIAL_JUDGEMENT">Editorial judgement</option><option value="STRATEGIC_RELEVANCE">Strategic relevance</option><option value="COMMUNITY_MOMENT">Community moment</option><option value="COMMERCIAL_PRIORITY">Commercial priority</option><option value="OTHER">Other</option>
              </select>
              <textarea value={overrideNote} onChange={(event) => setOverrideNote(event.target.value)} placeholder="Optional note for the audit trail" />
              <button className="button button-primary button-large" disabled={busy === "campaign"} onClick={createCampaign}><Sparkles /> {busy === "campaign" ? "Preparing…" : "CREATE CAMPAIGN"}</button>
            </div>
          </Card>
          {notice && <div className="toast">{notice}</div>}
        </>
      );
    return (
      <>
        <button className="back-link" onClick={() => navigate("/discover")}> 
          <ArrowLeft /> Back to Discover
        </button>
        <PageIntro
          eyebrow="CREATIVE DIRECTOR"
          title="Reviewing the evidence…"
          body="Minty is checking fit, freshness, evidence, risk and the strongest format available right now."
        />
        <Card className="review-generating">
          <Sparkles />
          <h2>Building the campaign…</h2>
          <p>
            No paid media is being generated. Nothing can publish from this
            step.
          </p>
        </Card>
        {notice && <div className="toast">{notice}</div>}
      </>
    );
  }

  if (!campaign)
    return (
      <>
        <button className="back-link" onClick={() => navigate("/create")}>
          <ArrowLeft /> Back to Create
        </button>
        <PageIntro
          eyebrow="CREATIVE DIRECTOR"
          title={
            busy === "campaign"
              ? "Reviewing the evidence…"
              : "One story. One clear campaign."
          }
          body={
            busy === "campaign"
              ? "Minty is checking fit, freshness, evidence and the best available format."
              : "Bring a story or your own idea. Minty makes the creative decisions."
          }
        />
        <div className="media-builder-grid">
          <Card>
            <Eyebrow>1 · SOURCE</Eyebrow>
            <div className="source-switch">
              <button
                className={sourceMode === "case" ? "active" : ""}
                onClick={() => setSourceMode("case")}
              >
                From a Case
              </button>
              <button
                className={sourceMode === "idea" ? "active" : ""}
                onClick={() => setSourceMode("idea")}
              >
                From my idea
              </button>
            </div>
            {sourceMode === "case" ? (
              <label className="large-field">
                Opportunity
                <select
                  value={storyId}
                  onChange={(event) => setStoryId(event.target.value)}
                >
                  {data.intelligence.slice(0, 30).map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.viral_score} · {item.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="large-field">
                Your idea
                <textarea
                  value={idea}
                  onChange={(event) => setIdea(event.target.value)}
                  placeholder="What should Detective Minty investigate, explain or react to?"
                />
              </label>
            )}
            {story && sourceMode === "case" && (
              <div className="source-preview">
                {story.editorial_policy?.eligible === false && <p>{story.editorial_policy.reason}</p>}
                <Score value={story.viral_score} />
                <div>
                  <strong>{story.recommendation}</strong>
                  <small>
                    {story.recommended_media_type ?? "Format pending"} · Minty
                    fit {story.minty_fit_score}
                  </small>
                </div>
              </div>
            )}
          </Card>
          <Card>
            <Eyebrow>2 · FORMAT</Eyebrow>
            <div className="compact-format-grid">
              {formats.map((item) => (
                <button
                  key={item.key}
                  className={type === item.key ? "active" : ""}
                  onClick={() => setType(item.key)}
                >
                  <item.icon />
                  <span>{item.label}</span>
                  {type === item.key && <Check />}
                </button>
              ))}
            </div>
            <div className="cost-confirm">
              <div>
                <span>Estimated now</span>
                <strong>$0.00</strong>
              </div>
              <p>
                Planning and the local template renderer are free. AI image and
                video providers are not connected, so Minty cannot spend on
                them.
              </p>
            </div>
            <button
              className="button button-primary button-large button-wide"
              onClick={createCampaign}
              disabled={
                busy === "campaign" ||
                (sourceMode === "case" && !storyId) ||
                (sourceMode === "idea" && idea.trim().length < 5)
              }
            >
              {busy === "campaign" ? "Preparing…" : "Create campaign plan"}
              <ArrowRight />
            </button>
          </Card>
        </div>
        {notice && <div className="toast">{notice}</div>}
      </>
    );

  if (campaign.director_decision && campaign.director_decision !== "CREATE")
    return (
      <>
        <button className="back-link" onClick={() => navigate("/discover")}>
          <ArrowLeft /> Back to Discover
        </button>
        <PageIntro
          eyebrow="CREATIVE DIRECTOR"
          title={
            campaign.director_decision === "WATCH"
              ? "Keep watching."
              : campaign.director_decision === "SKIP"
                ? "Skip this one."
                : "Verify before creating."
          }
          body={campaign.decision_reason ?? campaign.recommendation.reason}
        />
        <Card className="campaign-brief">
          <div>
            <Eyebrow>THE STORY</Eyebrow>
            <h2>{campaign.title}</h2>
            <p>{campaign.minty_angle}</p>
          </div>
          <div>
            <Eyebrow>DECISION</Eyebrow>
            <Status
              tone={
                campaign.director_decision === "NEEDS VERIFICATION"
                  ? "warn"
                  : "neutral"
              }
            >
              {campaign.director_decision}
            </Status>
            <small>No media was generated. No cost was incurred.</small>
          </div>
        </Card>
        <button
          className="button button-subtle"
          onClick={() => navigate("/discover")}
        >
          Review other stories
        </button>
        {notice && <div className="toast">{notice}</div>}
      </>
    );

  return (
    <>
      <button className="back-link" onClick={() => setParams({})}>
        <ArrowLeft /> New campaign
      </button>
      <PageIntro
        eyebrow={
          campaign.status === "APPROVED"
            ? "READY TO GO"
            : campaign.is_demo
              ? "SAFE INTERNAL DEMO"
              : "CAMPAIGN REVIEW"
        }
        title={campaign.title}
        body={
          campaign.status === "APPROVED"
            ? "Everything is checked and ready. Choose where and when."
            : "Review the finished campaign exactly as it will appear—then approve it or ask Minty to try again."
        }
        actions={campaign.status !== "APPROVED" ? (
          <div className="campaign-status">
            <Status>{campaign.status}</Status>
            <strong>${Number(campaign.estimated_cost).toFixed(2)}</strong>
            <small>campaign cost</small>
          </div>
        ) : undefined}
      />
      {campaign.status !== "APPROVED" && <section className="platform-campaign-review">
        {(["X", "INSTAGRAM", "TELEGRAM"] as const).map((channel) => {
          const channelAssets = platformAssets(channel);
          const copyKey = channel.toLowerCase();
          const body = String(campaign.copy_package[copyKey] ?? "");
          const limit = channel === "X" ? 280 : channel === "TELEGRAM" ? 1024 : null;
          const connection = platformConnection(channel);
          return (
            <Card className="platform-package" key={channel}>
              <div className="platform-package-head">
                <div><Eyebrow>{channel}</Eyebrow><h2>Complete {channel === "X" ? "post" : channel === "INSTAGRAM" ? "carousel" : "update"}</h2></div>
                <Status tone={connection === "CONNECTED" || connection === "HEALTHY" ? "good" : "warn"}>{connection}</Status>
              </div>
              {connection !== "CONNECTED" && connection !== "HEALTHY" && <p className="connection-note">Creative is ready for review. Publishing stays unavailable until this account connection is healthy.</p>}
              {channelAssets[0] ? (
                <div className={`platform-preview-grid platform-${channel.toLowerCase()}`}>
                  <div className="platform-preview">
                    <div className="preview-chrome"><span>mintycoin</span><small>{channel === "INSTAGRAM" && channelAssets.length > 1 ? `${channelAssets.length}-slide carousel` : "Editorial preview"}</small></div>
                    <img src={`/api/media/assets/${channelAssets[0].id}/file?variant=preview`} alt={`${channel} finished social media asset`} />
                  </div>
                  {channel === "INSTAGRAM" && channelAssets.length > 1 && <div className="carousel-order-editor">
                    <div className="carousel-order-heading"><strong>Arrange carousel</strong><small>Drag slides, or use the arrows. Order saves automatically.</small></div>
                    <div className="carousel-filmstrip carousel-sortable" aria-label={`${channelAssets.length} finished Instagram carousel slides`}>
                      {channelAssets.map((asset, index) => <div className={`carousel-slide-control ${draggedSlide === asset.id ? "is-dragging" : ""}`} key={asset.id} draggable={campaign.status !== "APPROVED" && !busy} onDragStart={() => setDraggedSlide(asset.id)} onDragEnd={() => setDraggedSlide(null)} onDragOver={(event) => event.preventDefault()} onDrop={() => dropCarouselSlide(asset.id)}>
                        <span className="carousel-position">{index + 1}</span>
                        <img src={`/api/media/assets/${asset.id}/file?variant=thumbnail`} alt={`Instagram carousel slide ${index + 1}`} />
                        {campaign.status !== "APPROVED" && <div className="carousel-move-actions">
                          <button type="button" aria-label={`Move slide ${index + 1} left`} disabled={index === 0 || busy === "carousel-order"} onClick={() => moveCarouselSlide(asset.id, -1)}><ArrowLeft /></button>
                          <button type="button" aria-label={`Move slide ${index + 1} right`} disabled={index === channelAssets.length - 1 || busy === "carousel-order"} onClick={() => moveCarouselSlide(asset.id, 1)}><ArrowRight /></button>
                        </div>}
                      </div>)}
                    </div>
                  </div>}
                </div>
              ) : <div className="review-generating"><Sparkles /><strong>{activeJobs ? "Creating finished media…" : "Media unavailable"}</strong></div>}
              <div className="platform-copy-panel">
                <div className="platform-copy-heading"><Eyebrow>FINISHED BODY COPY</Eyebrow><strong className={limit && [...body].length > limit ? "over-limit" : ""}>{[...body].length}{limit ? ` / ${limit}` : " characters"}</strong></div>
                <p>{body}</p>
                <div className="review-checks"><span><Check /> Facts {campaign.checks?.facts ?? "PENDING"}</span><span><Check /> Minty voice {campaign.checks?.voice ?? "PENDING"}</span><span><Check /> Visual impact {channelAssets[0]?.impact_check?.status ?? "PENDING"}</span></div>
              </div>
            </Card>
          );
        })}
      </section>}
      {campaign.status !== "APPROVED" ? (
        <Card className="campaign-decision">
          <div>
            <Eyebrow>YOUR DECISION</Eyebrow>
            <h2>Ready to use?</h2>
            <p>
              Approval locks this exact campaign version. It still cannot
              publish until you explicitly choose where and when.
            </p>
          </div>
          <div className="decision-actions">
            {detailLoadError && <div className="approval-blocker" role="status"><strong>Connection interrupted while checking media.</strong><span>Minty is trying again automatically. Your finished images are safe.</span><button className="button button-subtle" onClick={() => void loadCampaignDetail()}>CHECK AGAIN</button></div>}
            {!approvalBlocker && uncheckedVisuals.length > 0 && <div className="approval-blocker" role="status">
              <strong>Check the visuals before approval.</strong>
              <button className="button button-subtle" disabled={Boolean(busy)} onClick={checkCampaignVisuals}>
                <ShieldCheck /> {busy === 'check-campaign-visuals' ? 'Checking…' : 'CHECK VISUAL CLAIMS'}
              </button>
            </div>}
            {approvalBlocker && (
              <div className="approval-blocker" role="status">
                <strong>{approvalBlocker}</strong>
                <span>{activeJobs ? `${activeJobCount} media ${activeJobCount === 1 ? "job is" : "jobs are"} active. ${oldestActiveSeconds > 180 ? "This is taking longer than expected; Minty will recover or mark a failed render so you can use the ready images." : "This page updates automatically."}` : "Your campaign and copy are safe."}</span>
                {!activeJobs && (
                  <div className="decision-actions">
                    {campaignDetail?.readiness.canUseReducedCarousel && (
                      <button className="button button-primary" disabled={busy === "use-ready-carousel"} onClick={useReadyCarousel}>
                        <Check /> {busy === "use-ready-carousel" ? "Saving…" : `USE ${campaignDetail.readiness.readyInstagramSlideIds.length} IMAGES`}
                      </button>
                    )}
                    <button className="button button-subtle" disabled={busy === "retry-missing"} onClick={retryMissingMedia}>
                      <RefreshCw /> {busy === "retry-missing" ? "Retrying…" : "RETRY MISSING MEDIA"}
                    </button>
                    <button className="button button-subtle" disabled={busy === "regenerate"} onClick={regenerateMedia}>
                      <RefreshCw /> {busy === "regenerate" ? "Rebuilding…" : "REBUILD ALL MEDIA"}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              className="button button-primary button-large"
              disabled={
                Boolean(busy) || uncheckedVisuals.length > 0 ||
                Boolean(approvalBlocker)
              }
              onClick={approveCampaign}
            >
              <Check /> {busy === "approve-campaign" ? "Approving…" : activeJobs ? "CREATING MEDIA…" : uncheckedVisuals.length ? "CHECK VISUALS FIRST" : "APPROVE"}
            </button>
            <details>
              <summary className="button button-subtle button-large">
                <RefreshCw /> TRY AGAIN
              </summary>
              <div className="feedback-grid">
                {[
                  "NOT FUNNY ENOUGH",
                  "TOO BORING",
                  "TOO CRYPTO",
                  "NOT MINTY ENOUGH",
                  "BETTER VISUAL",
                  "STRONGER HOOK",
                  "START AGAIN",
                ].map((item) => (
                  <button
                    key={item}
                    className={feedback.includes(item) ? "active" : ""}
                    onClick={() =>
                      setFeedback((current) =>
                        current.includes(item)
                          ? current.filter((value) => value !== item)
                          : [...current, item].slice(-3),
                      )
                    }
                  >
                    {item}
                  </button>
                ))}
                <textarea
                  value={feedbackNote}
                  onChange={(event) => setFeedbackNote(event.target.value)}
                  placeholder="Or tell Minty what you don’t like…"
                />
                <button
                  className="button button-primary"
                  disabled={
                    (!feedback.length && !feedbackNote.trim()) ||
                    busy === "revise"
                  }
                  onClick={tryAgain}
                >
                  {busy === "revise" ? "Creating V2…" : "Create new version"}
                </button>
              </div>
            </details>
          </div>
        </Card>
      ) : distributionStage === "choose" ? (
        <Card className="distribution-card">
          <div className="distribution-heading">
            <Eyebrow>READY TO GO</Eyebrow>
            <h2>Where?</h2>
            <p>Minty selected every healthy channel with finished creative.</p>
          </div>
          <div className="channel-choice-grid">
            {campaignChannels.map((channel) => {
              const ready = channelReady(channel);
              const selected = selectedChannels.includes(channel);
              return (
                <button
                  key={channel}
                  disabled={!ready}
                  className={selected ? "selected" : ""}
                  onClick={() =>
                    setSelectedChannels((current) =>
                      current.includes(channel)
                        ? current.filter((item) => item !== channel)
                        : [...current, channel],
                    )
                  }
                >
                  <span className="channel-monogram">
                    {channel === "INSTAGRAM" ? "IG" : channel === "TELEGRAM" ? "TG" : "X"}
                  </span>
                  <span>
                    <strong>{channel === "INSTAGRAM" ? "Instagram" : channel === "TELEGRAM" ? "Telegram" : "X"}</strong>
                    <small>{channelReason(channel)}</small>
                  </span>
                  {selected && <Check />}
                </button>
              );
            })}
          </div>
          <div className="distribution-heading when-heading">
            <h2>When?</h2>
          </div>
          <div className="timing-choice-grid">
            <button
              className="timing-primary"
              disabled={!selectedChannels.length}
              onClick={() => chooseTiming("now")}
            >
              <Send />
              <span><strong>POST NOW</strong><small>Send after final confirmation</small></span>
              <ChevronRight />
            </button>
            <button
              className={timing === "schedule" ? "selected" : ""}
              disabled={!selectedChannels.length}
              onClick={() => setTiming("schedule")}
            >
              <CalendarDays />
              <span><strong>SCHEDULE</strong><small>Choose a date and time</small></span>
              <ChevronRight />
            </button>
          </div>
          {timing === "schedule" && (
            <div className="schedule-choice">
              <label>
                Date and time
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  min={new Date(
                    Date.now() +
                      60_000 -
                      new Date().getTimezoneOffset() * 60_000,
                  )
                    .toISOString()
                    .slice(0, 16)}
                  onChange={(event) => setScheduledAt(event.target.value)}
                />
              </label>
              <button
                className="button button-primary button-large"
                disabled={!scheduledAt || new Date(scheduledAt).getTime() <= Date.now()}
                onClick={() => setDistributionStage("confirm")}
              >
                Review schedule <ArrowRight />
              </button>
            </div>
          )}
        </Card>
      ) : distributionStage === "confirm" ? (
        <Card className="distribution-confirm">
          <button className="back-link" onClick={() => setDistributionStage("choose")}>
            <ArrowLeft /> Change selection
          </button>
          <Eyebrow>FINAL CONFIRMATION</Eyebrow>
          <h2>{campaign.title}</h2>
          <strong>{selectedChannels.length} {selectedChannels.length === 1 ? "channel" : "channels"}</strong>
          <div className="confirmation-channels">
            {selectedChannels.map((channel) => <span key={channel}><Check />{channel === "INSTAGRAM" ? "Instagram" : channel === "TELEGRAM" ? "Telegram" : "X"}</span>)}
          </div>
          <div className="confirmation-time">
            {timing === "now" ? <Send /> : <Clock3 />}
            <span>
              <small>WHEN</small>
              <strong>{timing === "now" ? "NOW" : new Date(scheduledAt).toLocaleString()}</strong>
            </span>
          </div>
          <p>Nothing is sent until you press POST.</p>
          <button
            className="button button-primary button-large button-wide distribution-post"
            disabled={busy === "distribution"}
            onClick={executeDistribution}
          >
            {busy === "distribution" ? "Sending…" : "POST"} <Send />
          </button>
        </Card>
      ) : (
        <Card className="distribution-result">
          <div className={`result-orb ${resultHasFailures ? "has-error" : ""}`}>
            {resultHasFailures ? "!" : <Check />}
          </div>
          <Eyebrow>{timing === "schedule" ? resultHasFailures ? resultHasSuccess ? "PARTIALLY SCHEDULED" : "NEEDS ATTENTION" : "SCHEDULED" : resultAllLive ? "LIVE" : resultHasFailures && resultHasSuccess ? "PARTIALLY LIVE" : resultHasFailures ? "NEEDS ATTENTION" : "ON ITS WAY"}</Eyebrow>
          <h2>{resultHasFailures ? `${failedChannelCount} ${failedChannelCount === 1 ? "channel needs" : "channels need"} attention.` : timing === "schedule" ? "Your campaign is scheduled." : resultAllLive ? "Your campaign is live." : "Minty is posting your campaign."}</h2>
          <div className="result-channels">
            {distributionStates.map((outcome) => {
              const failed = ["FAILED", "ERROR", "NOT_SENT"].includes(outcome.observed);
              const live = outcome.observed === "PUBLISHED";
              return (
                <div key={outcome.platform}>
                  <span>{outcome.platform === "INSTAGRAM" ? "Instagram" : outcome.platform === "TELEGRAM" ? "Telegram" : "X"}</span>
                  <strong className={failed ? "result-error" : ""}>{failed ? "Needs attention" : timing === "schedule" ? "Scheduled" : live ? "Live ✓" : "Posting…"}</strong>
                  {failed && outcome.error && <small>{outcome.error}</small>}
                  {failed ? <button className="text-button" disabled={busy === `retry-${outcome.platform}`} onClick={() => retryDistribution(outcome)}>{outcome.contentId ? `RETRY ${outcome.platform}` : `REVIEW ${outcome.platform}`}</button> : outcome.postUrl ? <a className="text-button" href={outcome.postUrl} target="_blank" rel="noreferrer">View post</a> : null}
                </div>
              );
            })}
          </div>
          <button className="button button-subtle button-large" onClick={() => navigate("/calendar")}>Done</button>
        </Card>
      )}
      <details className="technical-panel campaign-advanced">
        <summary>
          Advanced campaign details <ChevronRight />
        </summary>
        <div>
          <Card className="campaign-brief">
            <div>
              <Eyebrow>THE ANGLE</Eyebrow>
              <h2>{campaign.minty_angle}</h2>
              <blockquote>{campaign.hook}</blockquote>
              <p>{campaign.decision_reason ?? campaign.recommendation.reason}</p>
            </div>
            <div>
              <Eyebrow>CREATIVE DIRECTION</Eyebrow>
              <strong>{campaign.available_now ?? campaign.recommendation.primary}</strong>
              {campaign.conceptual_best !== campaign.available_now && <small>Ideal: {campaign.conceptual_best}. Best available now: {campaign.available_now}.</small>}
              <small>Campaign V{campaign.version} · {campaign.supporting_formats?.join(" · ")}</small>
            </div>
          </Card>
          <button
            className="button button-subtle"
            disabled={busy === "regenerate" || activeJobs}
            onClick={regenerateMedia}
          >
            <RefreshCw /> {busy === "regenerate" ? "Rebuilding…" : "Rebuild media"}
          </button>
          {campaign.visual_direction?.mode && (
            <section className="media-section">
              <div className="section-line">
                <div>
                  <Eyebrow>STORY-FIRST ART DIRECTION</Eyebrow>
                  <h2>{String(campaign.visual_direction.mode).replaceAll("_", " ")}</h2>
                </div>
                <Status>
                  FATIGUE {Number(campaign.visual_direction.creativeFatigueScore ?? 0)} / 100
                </Status>
              </div>
              <p>{campaign.visual_direction.visualConcept}</p>
              <small>{campaign.visual_direction.rationale}</small>
            </section>
          )}
          <div className="campaign-tabs">
            <button
              onClick={() =>
                document.getElementById("concepts")?.scrollIntoView()
              }
            >
              Concepts
            </button>
            <button
              onClick={() =>
                document.getElementById("storyboards")?.scrollIntoView()
              }
            >
              Storyboards
            </button>
            <button
              onClick={() =>
                document.getElementById("assets")?.scrollIntoView()
              }
            >
              Media
            </button>
            <button
              onClick={() => document.getElementById("copy")?.scrollIntoView()}
            >
              Copy
            </button>
          </div>
          <section id="concepts" className="media-section">
            <div className="section-line">
              <div>
                <Eyebrow>MEME ENGINE</Eyebrow>
                <h2>Three deliberate concepts.</h2>
              </div>
              <span>Choose one. Quality over quantity.</span>
            </div>
            <div className="concept-grid">
              {campaign.meme_concepts.map((concept, index) => (
                <Card key={concept.id}>
                  <span className="concept-number">0{index + 1}</span>
                  <h3>{concept.concept}</h3>
                  <p>{concept.visual}</p>
                  <blockquote>{concept.overlay}</blockquote>
                  <small>{concept.why}</small>
                  <button
                    className="button button-subtle button-wide"
                    disabled={busy === "render"}
                    onClick={() => render(concept)}
                  >
                    <WandSparkles /> Render this concept
                  </button>
                </Card>
              ))}
            </div>
          </section>
          <section id="storyboards" className="media-section">
            <div className="section-line">
              <div>
                <Eyebrow>CAROUSEL</Eyebrow>
                <h2>Six slides. One visual system.</h2>
              </div>
              <Status>STORYBOARD READY</Status>
            </div>
            <div className="slide-strip">
              {campaign.carousel_storyboard.map((slide) => (
                <Card key={slide.slide}>
                  <span>{String(slide.slide).padStart(2, "0")}</span>
                  <small>{slide.role}</small>
                  <strong>{slide.text}</strong>
                  <p>{slide.visual}</p>
                </Card>
              ))}
            </div>
            <div className="section-line video-line">
              <div>
                <Eyebrow>SHORT VIDEO · 15 SECONDS</Eyebrow>
                <h2>Storyboard before generation.</h2>
              </div>
              <Status
                tone={
                  data.media.storage.videoRenderingAllowed ? "good" : "warn"
                }
              >
                {data.media.storage.videoRenderingAllowed
                  ? "RENDER CAPACITY READY"
                  : "STORYBOARD ONLY"}
              </Status>
            </div>
            <div className="storyboard-list">
              {campaign.video_storyboard.map((scene) => (
                <div key={scene.scene}>
                  <span>{scene.scene}</span>
                  <div>
                    <strong>{scene.visual}</strong>
                    <small>
                      {scene.duration}s · {scene.onscreen}
                    </small>
                    <p>{scene.voiceover}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="video-guard">
              <Film />
              <div>
                <strong>
                  {data.media.storage.videoRenderingAllowed
                    ? "Storyboard ready for an approved provider."
                    : "Video rendering is safely paused."}
                </strong>
                <p>
                  {data.media.storage.videoRenderingAllowed
                    ? "No generative video provider is connected. Approving this storyboard will not spend or publish."
                    : "The VPS free-space threshold is below the safe video floor. Your storyboard is preserved."}
                </p>
              </div>
              <button className="button button-subtle" disabled>
                Generate video
              </button>
            </div>
          </section>
          <section className="media-section render-panel">
            <Card>
              <div>
                <Eyebrow>LOCAL TEMPLATE RENDERER · $0.00</Eyebrow>
                <h2>Make the first visual.</h2>
                <p>
                  Important text is rendered programmatically for accurate
                  spelling and consistent Minty typography.
                </p>
              </div>
              <label>
                Template
                <select
                  value={template}
                  onChange={(event) => setTemplate(event.target.value)}
                >
                  {data.media.templates.map((item) => (
                    <option value={item.template_key} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Format
                <select
                  value={ratio}
                  onChange={(event) => setRatio(event.target.value)}
                >
                  <option value="1:1">Square · 1:1</option>
                  <option value="4:5">Portrait · 4:5</option>
                  <option value="9:16">Story / Reel · 9:16</option>
                  <option value="16:9">Landscape · 16:9</option>
                </select>
              </label>
              <button
                className="button button-primary button-large"
                disabled={
                  busy === "render" ||
                  !data.media.storage.imageGenerationAllowed
                }
                onClick={() => render()}
              >
                {busy === "render" ? "Queueing…" : "Render visual"}
                <Sparkles />
              </button>
            </Card>
          </section>
          <section id="assets" className="media-section">
            <div className="section-line">
              <div>
                <Eyebrow>CAMPAIGN MEDIA</Eyebrow>
                <h2>
                  {assets.length
                    ? `${assets.length} version${assets.length === 1 ? "" : "s"}`
                    : "Your first visual will appear here."}
                </h2>
              </div>
              {activeJobs && <Status tone="warn">RENDERING</Status>}
            </div>
            {jobs
              .filter((job) => job.state === "FAILED")
              .map((job) => (
                <div className="soft-warning" key={job.id}>
                  Rendering failed. Your project and storyboard are safe. Try
                  again or view technical details.
                </div>
              ))}
            <div className="asset-review-grid">
              {assets.map((asset) => (
                <AssetReview
                  key={asset.id}
                  asset={asset}
                  platform={platform}
                  setPlatform={setPlatform}
                  busy={busy}
                  action={assetAction}
                  variation={() =>
                    render({
                      concept: asset.title,
                      overlay: asset.prompt ?? asset.title,
                      sourceAssetId: asset.id,
                    })
                  }
                />
              ))}
            </div>
            {!assets.length && !activeJobs && (
              <Empty
                title="No rendered media yet"
                body="Choose one concept above or use the local renderer. No paid provider is required."
              />
            )}
          </section>
          <section id="copy" className="media-section">
            <div className="section-line">
              <div>
                <Eyebrow>CAMPAIGN COPY</Eyebrow>
                <h2>Ready to edit, check and approve.</h2>
              </div>
            </div>
            <div className="copy-package-grid">
              {Object.entries(campaign.copy_package)
                .filter(([, value]) => typeof value === "string")
                .map(([key, value]) => (
                  <Card key={key}>
                    <Status>{key}</Status>
                    <p>{String(value)}</p>
                    <button
                      className="text-button"
                      onClick={() =>
                        navigator.clipboard.writeText(String(value))
                      }
                    >
                      <Copy /> Copy
                    </button>
                  </Card>
                ))}
            </div>
            <Card className="content-handoff">
              <div>
                <Eyebrow>NEXT · HUMAN REVIEW</Eyebrow>
                <h3>Prepare the platform draft.</h3>
                <p>
                  Approved DISTRO-ready media will be attached. The draft still
                  requires text claim checking, human approval and an explicit
                  Now/Later decision.
                </p>
              </div>
              <label>
                Platform
                <select
                  value={destination}
                  onChange={(event) => setDestination(event.target.value)}
                >
                  <option value="INSTAGRAM">Instagram</option>
                  <option value="TELEGRAM">Telegram</option>
                  <option value="X">X</option>
                </select>
              </label>
              <button
                className="button button-primary button-large"
                onClick={prepareContent}
                disabled={
                  busy === "content" ||
                  (assets.length > 0 &&
                    !assets.some(
                      (asset) =>
                        asset.status === "APPROVED" && asset.distro_media_id,
                    ))
                }
              >
                {busy === "content" ? "Preparing…" : "Open approval draft"}
                <ArrowRight />
              </button>
            </Card>
          </section>
        </div>
      </details>
      <p className="manual-only">
        <LockKeyhole /> LIVE — MANUAL APPROVAL ONLY. Nothing publishes without
        approval and an explicit Now/Later action.
      </p>
      {notice && <div className="toast">{notice}</div>}
    </>
  );
}

function AssetReview({
  asset,
  platform,
  setPlatform,
  busy,
  action,
  variation,
}: {
  asset: MediaAsset;
  platform: string;
  setPlatform: (value: string) => void;
  busy: string;
  action: (
    asset: MediaAsset,
    action: "check" | "approve" | "handoff" | "archive",
  ) => Promise<void>;
  variation: () => void;
}) {
  const checked = ["PASS", "ADVISORY"].includes(
    asset.claim_check?.status ?? "",
  );
  const approved = asset.status === "APPROVED" || asset.status === "PUBLISHED";
  return (
    <Card className="asset-review">
      <div className={`platform-preview preview-${platform.toLowerCase()}`}>
        <div className="preview-chrome">
          <span>
            {platform === "INSTAGRAM"
              ? "mintycoin"
              : platform === "X"
                ? "@TheMintyCoin"
                : "MINTY"}
          </span>
          <small>Editorial preview</small>
        </div>
        <img
          src={`/api/media/assets/${asset.id}/file?variant=preview`}
          alt={asset.title}
        />
        <p>{asset.title}</p>
      </div>
      <div className="preview-tabs">
        {["INSTAGRAM", "X", "TELEGRAM", "VERTICAL"].map((item) => (
          <button
            className={platform === item ? "active" : ""}
            onClick={() => setPlatform(item)}
            key={item}
          >
            {item}
          </button>
        ))}
      </div>
      <div className="asset-meta">
        <div>
          <Eyebrow>
            {asset.asset_type} · V{asset.version}
          </Eyebrow>
          <h3>{asset.title}</h3>
          <p>
            {asset.aspect_ratio} · {asset.provenance} · $
            {Number(asset.actual_cost).toFixed(2)}
          </p>
        </div>
        <Status>{asset.status}</Status>
      </div>
      <div className="minty-check">
        <BadgeCheck />
        <div>
          <strong>{asset.visual_check?.status ?? "NEEDS ATTENTION"}</strong>
          <small>Minty Check · Visual Bible V2</small>
        </div>
      </div>
      {asset.claim_check?.flags?.map((flag) => (
        <div className="soft-warning" key={flag}>
          {flag}
        </div>
      ))}
      <div className="asset-actions">
        {!checked && (
          <button
            className="button button-primary"
            disabled={busy.includes(asset.id)}
            onClick={() => action(asset, "check")}
          >
            <ShieldCheck /> Visual claim check
          </button>
        )}
        {checked && !approved && (
          <button
            className="button button-primary"
            disabled={busy.includes(asset.id)}
            onClick={() => action(asset, "approve")}
          >
            <Check /> Approve V{asset.version}
          </button>
        )}
        {approved && !asset.distro_media_id && (
          <button
            className="button button-primary"
            disabled={busy.includes(asset.id)}
            onClick={() => action(asset, "handoff")}
          >
            <Package /> Prepare for DISTRO-NINE
          </button>
        )}
        {asset.distro_media_id && <Status tone="good">DISTRO READY</Status>}
        <button className="button button-subtle" onClick={variation}>
          <RefreshCw /> Variation
        </button>
        <a
          className="button button-subtle"
          href={`/api/media/assets/${asset.id}/file?variant=file`}
          download
        >
          <Download /> Download
        </a>
        <button
          className="text-button"
          onClick={() => action(asset, "archive")}
        >
          <Archive /> Archive
        </button>
      </div>
      <details>
        <summary>Details</summary>
        <dl>
          <dt>Provider</dt>
          <dd>
            {asset.provider} · {asset.model}
          </dd>
          <dt>Provenance</dt>
          <dd>{asset.provenance}</dd>
          <dt>Prompt / overlay</dt>
          <dd>{asset.prompt}</dd>
          <dt>Created</dt>
          <dd>{new Date(asset.created_at).toLocaleString()}</dd>
          <dt>Sources</dt>
          <dd>
            {asset.source_urls.length
              ? asset.source_urls.join(", ")
              : "No external claim source attached."}
          </dd>
        </dl>
      </details>
    </Card>
  );
}

export function MediaLibrary({ data, refresh }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [type, setType] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [notice, setNotice] = useState("");
  const assets = useMemo(
    () =>
      data.media.assets
        .filter((asset) =>
          `${asset.title} ${asset.tags.join(" ")} ${asset.provider}`
            .toLowerCase()
            .includes(query.toLowerCase()),
        )
        .filter((asset) => type === "ALL" || asset.asset_type === type)
        .filter((asset) => status === "ALL" || asset.status === status),
    [data.media.assets, query, type, status],
  );
  return (
    <>
      <PageIntro
        eyebrow="MEDIA"
        title="The Minty media library."
        body="Every visual, version and approval—searchable without exposing drafts publicly."
        actions={
          <button
            className="button button-primary"
            onClick={() => navigate("/create")}
          >
            <Plus /> Create media
          </button>
        }
      />
      <div className="media-library-toolbar">
        <div className="search-field">
          <Search />
          <input
            aria-label="Search media"
            placeholder="Search media, Case, campaign or tag"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div>
          {["ALL", "IMAGE", "MEME", "CAROUSEL_SLIDE", "VIDEO"].map((item) => (
            <button
              className={type === item ? "active" : ""}
              onClick={() => setType(item)}
              key={item}
            >
              {item.replace("_", " ")}
            </button>
          ))}
        </div>
        <div>
          {["ALL", "GENERATED", "APPROVED", "PUBLISHED"].map((item) => (
            <button
              className={status === item ? "active" : ""}
              onClick={() => setStatus(item)}
              key={item}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      <div className="library-grid">
        {assets.map((asset) => (
          <button
            key={asset.id}
            onClick={() =>
              navigate(
                `/create/media?package=${asset.campaign_package_id ?? ""}`,
              )
            }
          >
            <img
              loading="lazy"
              src={`/api/media/assets/${asset.id}/file?variant=thumbnail`}
              alt=""
            />
            <div>
              <Status>{asset.status}</Status>
              <h3>{asset.title}</h3>
              <p>
                {asset.asset_type} · V{asset.version} · {asset.aspect_ratio}
              </p>
              <small>
                {asset.provider} · ${Number(asset.actual_cost).toFixed(2)}
              </small>
            </div>
          </button>
        ))}
      </div>
      {!assets.length && (
        <Empty
          title="No matching media"
          body="Try a broader filter or create the first visual."
        />
      )}
      <StorageCard data={data} />
      {notice && <div className="toast">{notice}</div>}
    </>
  );
}

export function BrandAssets({ data, refresh }: Props) {
  const [category, setCategory] = useState("Detective Minty");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice("");
    const form = new FormData(event.currentTarget);
    form.set("category", category);
    try {
      await upload("/media/upload", form);
      await refresh();
      event.currentTarget.reset();
      setNotice(
        "Brand asset uploaded. It will not become canon until explicitly marked as an approved reference.",
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const bible = data.media.visualBible?.content;
  return (
    <>
      <PageIntro
        eyebrow="MEDIA · BRAND ASSETS"
        title="Keep Minty recognisable."
        body="MintyCoin.org is the visual authority. Only approved references shape Detective Minty canon; originals are preserved."
      />
      <Card className="visual-bible">
        <div>
          <BookOpen />
          <div>
            <Eyebrow>
              MINTY INTELLIGENCE VISUAL SYSTEM · V
              {data.media.visualBible?.version ?? 1}
            </Eyebrow>
            <h2>One brand world. Every public touchpoint.</h2>
            <p>
              Extreme whitespace, precise typography, graphite, fine rules and
              restrained violet identify Minty before the logo. Detective Minty
              appears selectively.
            </p>
          </div>
        </div>
        <div className="bible-palette">
          {Object.entries(bible?.colours ?? bible?.palette ?? {}).map(
            ([name, value]) => (
              <span
                key={name}
                style={{ background: String(value) }}
                title={name}
              />
            ),
          )}
        </div>
        <details>
          <summary>View the canon</summary>
          <div className="bible-grid">
            <div>
              <strong>Brand authority</strong>
              <p>
                {bible?.authority?.principle ??
                  "MintyCoin.org defines the public visual world."}
              </p>
            </div>
            <div>
              <strong>Detective Minty</strong>
              <p>{bible?.detectiveMinty?.role ?? bible?.character?.identity}</p>
            </div>
            <div>
              <strong>Typography</strong>
              <p>
                {bible?.typography?.headlines ?? bible?.typography?.guidance}
              </p>
            </div>
            <div>
              <strong>Never</strong>
              <p>
                {(bible?.imagery?.never ?? bible?.character?.never ?? []).join(
                  " · ",
                )}
              </p>
            </div>
          </div>
        </details>
      </Card>
      <div className="brand-layout">
        <Card>
          <Eyebrow>ADD BRAND ASSET</Eyebrow>
          <h2>Upload an approved source file.</h2>
          <form onSubmit={submit}>
            <label className="large-field">
              Category
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                {[
                  "Detective Minty",
                  "Squirrel Squad",
                  "San Nutsford",
                  "Logos",
                  "Backgrounds",
                  "Evidence Room",
                  "Icons",
                  "Textures",
                  "Templates",
                  "Approved Media",
                ].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label className="large-field">
              Name
              <input
                name="title"
                minLength={2}
                required
                placeholder="e.g. Detective Minty canonical portrait"
              />
            </label>
            <label className="large-field">
              Tags
              <input name="tags" placeholder="portrait, coat, canonical" />
            </label>
            <label className="upload-drop">
              <Upload />
              <strong>Choose JPEG, PNG or WebP</strong>
              <small>Maximum 15 MB · executable files are rejected</small>
              <input
                type="file"
                name="file"
                accept="image/jpeg,image/png,image/webp"
                required
              />
            </label>
            <label className="check-field">
              <input type="checkbox" name="approvedReference" value="true" />{" "}
              Mark as approved character reference
            </label>
            <label className="check-field">
              <input type="checkbox" name="canonical" value="true" /> Mark as
              canonical brand asset
            </label>
            <button
              className="button button-primary button-wide"
              disabled={busy}
            >
              {busy ? "Uploading…" : "Upload asset"}
            </button>
          </form>
        </Card>
        <div>
          <div className="asset-category-tabs">
            {[
              "Detective Minty",
              "Squirrel Squad",
              "San Nutsford",
              "Logos",
              "Backgrounds",
              "Evidence Room",
              "Icons",
              "Textures",
              "Templates",
              "Approved Media",
            ].map((item) => (
              <button
                key={item}
                className={category === item ? "active" : ""}
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="brand-grid">
            {data.media.brandAssets
              .filter((item) => item.category === category)
              .map((asset) => (
                <Card key={asset.id}>
                  {asset.preview_key ? (
                    <img
                      src={`/api/media/assets/${String(asset.metadata.mediaAssetId)}/file?variant=preview`}
                      alt={asset.name}
                    />
                  ) : (
                    <div className="brand-placeholder">
                      <Images />
                    </div>
                  )}
                  <h3>{asset.name}</h3>
                  <p>{asset.provenance}</p>
                  <div>
                    {asset.canonical && <Status tone="good">CANONICAL</Status>}
                    {asset.approved_reference && (
                      <Status tone="good">APPROVED REFERENCE</Status>
                    )}
                  </div>
                </Card>
              ))}
          </div>
          {!data.media.brandAssets.some(
            (item) => item.category === category,
          ) && (
            <Empty
              title={`No ${category} assets yet`}
              body="Upload approved source artwork. Nothing becomes canon automatically."
            />
          )}
        </div>
      </div>
      {notice && <div className="toast">{notice}</div>}
    </>
  );
}

function StorageCard({ data }: { data: Bootstrap }) {
  const storage = data.media.storage;
  return (
    <Card className="storage-card">
      <FolderOpen />
      <div>
        <Eyebrow>PRIVATE MEDIA STORAGE</Eyebrow>
        <h3>
          {formatBytes(storage.usedBytes)} of {formatBytes(storage.quotaBytes)}{" "}
          Minty quota used
        </h3>
        <p>
          {formatBytes(storage.freeBytes)} free on the VPS · Images{" "}
          {storage.imageGenerationAllowed ? "allowed" : "paused"} · Video{" "}
          {storage.videoRenderingAllowed ? "allowed" : "paused"}
        </p>
      </div>
      <Status tone={storage.warning ? "warn" : "good"}>
        {storage.warning ? "LOW DISK WARNING" : "HEALTHY"}
      </Status>
    </Card>
  );
}
function formatBytes(value: number) {
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(1)} GB`;
  if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(value / 1024)} KB`;
}

function slideOrder(asset: MediaAsset) {
  const tag = asset.tags.find((value) => /^SLIDE \d+ OF \d+$/i.test(value));
  return tag ? Number(tag.match(/\d+/)?.[0] ?? 999) : 999;
}
